import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPencilAlt,
    faGlasses,
    faBookOpen,
    faGraduationCap,
    faFeatherPointed,
    faPenNib,
    faPaintbrush,
    faHeadphones,
    faHeadphonesSimple,
    faEarListen,
    faLock,
} from "@fortawesome/free-solid-svg-icons";
import TurtleIcon from "../AssistantWidget/TurtleIcon";
import { Achievement, AchievementIcon } from "../../utils/achievements";
import styles from "./achievementBadge.module.scss";
import { useTranslation } from "react-i18next";
import { useProgressText } from "../../i18n/progressText";

const ACCESSORY_ICONS: Record<AchievementIcon, typeof faPencilAlt> = {
    pencil: faPencilAlt,
    glasses: faGlasses,
    book: faBookOpen,
    cap: faGraduationCap,
    // Soft, flowing feather for hiragana; angular pen nib for katakana.
    feather: faFeatherPointed,
    nib: faPenNib,
    // A calligraphy brush for kanji.
    brush: faPaintbrush,
    // Listening missions: over-ear headphones for hiragana, in-ear earbuds
    // for katakana, a listening ear for kanji.
    headphones: faHeadphones,
    earbuds: faHeadphonesSimple,
    ear: faEarListen,
};

// At the small sizes these badges render at (as little as ~28px, e.g. the
// homepage row), the accessory glyph alone is too small to read apart from
// the others - a distinct color per achievement makes them tell apart at a
// glance even when the icon shape itself is illegible.
const ACCESSORY_COLORS: Record<AchievementIcon, string> = {
    pencil: "#d7263d",
    glasses: "#1d6fd6",
    book: "#7c3aed",
    cap: "#b45309",
    feather: "#be185d",
    nib: "#0f766e",
    // Ink - the one colour not already taken by another accessory.
    brush: "#1e293b",
    headphones: "#ea580c",
    earbuds: "#0891b2",
    ear: "#65a30d",
};

type AchievementBadgeProps = {
    achievement: Achievement;
    // Locked badges are shown too (in the profile's full list) so a learner
    // can see what's still to earn - dimmed, with a lock instead of the
    // achievement's own accessory icon.
    locked?: boolean;
    size?: number;
};

// A round かめ (turtle) badge for one achievement - the same mascot used
// everywhere else in the app (header logo, かめ assistant), so an unlocked
// achievement reads as unmistakably part of KanaLearn rather than a generic
// trophy icon. The small accessory icon in the corner (pencil, glasses,
// book, cap, feather, pen nib, brush, headphones, earbuds, ear) is what
// actually distinguishes one achievement from another.
function AchievementBadge({ achievement, locked = false, size = 48 }: AchievementBadgeProps) {
    const { t } = useTranslation("common");
    const { achievementTitle, achievementDescription } = useProgressText();
    const accessoryIcon = locked ? faLock : ACCESSORY_ICONS[achievement.icon];
    const accessorySize = Math.max(size * 0.5, 16);
    const label = t(locked ? "achievementBadge.lockedLabel" : "achievementBadge.label", {
        title: achievementTitle(achievement),
        description: achievementDescription(achievement),
    });

    return (
        <div
            className={locked ? `${styles.badge} ${styles.badgeLocked}` : styles.badge}
            style={{ width: size, height: size }}
            role="img"
            aria-label={label}
            title={label}
        >
            <TurtleIcon size={size} className={styles.badgeTurtle} animated={false} />
            <span
                className={styles.badgeAccessory}
                style={{
                    width: accessorySize,
                    height: accessorySize,
                    fontSize: accessorySize * 0.58,
                    color: locked ? undefined : ACCESSORY_COLORS[achievement.icon],
                }}
            >
                <FontAwesomeIcon icon={accessoryIcon} />
            </span>
        </div>
    );
}

export default AchievementBadge;
